import { ColumnDef } from '@tanstack/react-table';
import { Controller, useFormContext } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import { DepartmentQueryData } from '@area-restrita/api-client/model/departmentQueryData';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

export type PermissionTableData = {
  id: number;
  tradingName: string;
} & Record<string, boolean>;

export const factoryColumns = (
  departments: DepartmentQueryData[],
): ColumnDef<PermissionTableData>[] => [
  {
    accessorKey: 'company',
    header: function DocumentPermissionHeader() {
      const { checkAuthorization } = useAuthSessionStore();
      const canUpdate = checkAuthorization(({ can }) =>
        can('update', 'document_permissions'),
      );

      return (
        <Controller
          name="all"
          render={({ field }) => (
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={!canUpdate}
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Empresas / Cargos
              </label>
            </div>
          )}
        />
      );
    },
    cell: function DocumentPermissionCompany({ row }) {
      const { checkAuthorization } = useAuthSessionStore();
      const canUpdate = checkAuthorization(({ can }) =>
        can('update', 'document_permissions'),
      );

      const { watch } = useFormContext();
      const all = watch('all');
      return (
        <Controller
          name={`company.${row.original.id}`}
          render={({ field }) => (
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={all || field.value}
                onCheckedChange={all ? undefined : field.onChange}
                disabled={!canUpdate}
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {row.original.tradingName}
              </label>
            </div>
          )}
        />
      );
    },
  },
  ...departments.map<ColumnDef<PermissionTableData>>((department) => ({
    accessorKey: department.name,
    header: function DocumentPermissionDepartment() {
      const { checkAuthorization } = useAuthSessionStore();
      const canUpdate = checkAuthorization(({ can }) =>
        can('update', 'document_permissions'),
      );
      const { watch } = useFormContext();
      const all = watch('all');

      return (
        <Controller
          name={`department.${department.id}`}
          render={({ field }) => (
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={all || field.value}
                onCheckedChange={all ? undefined : field.onChange}
                disabled={!canUpdate}
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {department.name}
              </label>
            </div>
          )}
        />
      );
    },
    cell: function DocumentPermissionCell({ row }) {
      const { checkAuthorization } = useAuthSessionStore();
      const canUpdate = checkAuthorization(({ can }) =>
        can('update', 'document_permissions'),
      );

      const { watch } = useFormContext();
      const all = watch('all');
      const roleValue = watch(`department.${department.id}`);
      const companyValue = watch(`company.${row.original.id}`);
      return (
        <Controller
          name={`granular.${department.id}.${row.original.id}`}
          render={({ field }) => (
            <Checkbox
              checked={all || roleValue || companyValue || field.value}
              onCheckedChange={
                all || roleValue || companyValue ? undefined : field.onChange
              }
              disabled={!canUpdate}
            />
          )}
        />
      );
    },
  })),
];
